import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import PageWithTitle from '../../components/atom/PageWithTitle';
import { useTranslation } from 'react-i18next';
import EditBookingDetails from './components/EditBookingDetails';
import BookingItemsDetails from './components/bookingitems/BookingItemsDetails';
import OrdersDetails from './components/orders/OrdersDetails';
import useNotify from '../../hooks/useNotify';
import {
    useGetBooking,
    cancelBooking,
    simpleRefundBooking,
    getBookings,
    editBooking,
} from '@openapi/bookings/bookings';
import { useGetOrders } from '@openapi/orders/orders';
import { useGetBookingItems } from '@openapi/booking-items/booking-items';
import { useGetBookingAuditLogs, useGetEmailLogs, resendEmail } from '@openapi/logs/logs';
import { useQueryClient } from '@tanstack/react-query';
import CustomFieldsDetails from './components/customfields/CustomFieldsDetails';
import {
    mapCustomFields,
    buildNotePayload,
    noteKey,
} from './components/customfields/helpers/customFields';
import { Alert, Box, Button, Chip, Grid2, IconButton, Tooltip, Typography } from '@mui/material';
import { BOOKING_STATUS_COLORS, BOOKING_STATUSES, ORDER_STATUSES } from '../../helpers/constants';
import { formatDateTimeInVenueTz } from '../../helpers/formatDateTime';
import { formatCentsToUsd } from '../../helpers/formatCentsToUsd';

import EditIcon from '@mui/icons-material/Edit';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CommonButton from '../../components/atom/CommonButton';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import { useCapabilities } from '../../hooks/useCapabilities';
import BookingAuditLogsBlock from './components/BookingAuditLogsBlock';
import BookingEmailLogsBlock from './components/BookingEmailLogsBlock';
import useTicketMatrixData from '../../hooks/useTicketMatrixData';
import useUnsavedChangesWarning from '../../hooks/useUnsavedChangesWarning';
import useNavigationGuard from '../../hooks/useNavigationGuard';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

// Contact lives as an editable draft layered over the server booking: the page
// owns the edits, derives dirty/valid during render, and merges them into the
// shared editBooking call - no forwardRef/imperative handle in the child.
const CONTACT_DEFAULT_CHANNELS = ['email'];
const pickContactFields = (b) => ({
    firstName: b?.firstName ?? '',
    lastName: b?.lastName ?? '',
    phone: b?.phone ?? '',
    email: b?.email ?? '',
    language: b?.language ?? '',
    checkinNote: b?.checkinNote ?? '',
    isReviewEmailSuppressed: b?.isReviewEmailSuppressed ?? false,
    notificationChannels: b?.notificationChannels ?? CONTACT_DEFAULT_CHANNELS,
});
// notificationChannels hold no duplicates, so set equality is length + membership.
const sameChannels = (a, b) => {
    const sa = a ?? [];
    const sb = b ?? [];
    return sa.length === sb.length && sa.every((c) => sb.includes(c));
};
const EMAIL_SCHEMA = Yup.string().nullable().email();

function EditBookingPageInner() {
    const { t } = useTranslation();
    const { can, CAPABILITIES } = useCapabilities();
    const canManageBookings = can(CAPABILITIES.BOOKINGS_MANAGE);
    const canRefundBookings = can(CAPABILITIES.BOOKINGS_REFUND);
    const canAlwaysRefund = can(CAPABILITIES.BOOKINGS_ALWAYS_REFUND);
    const canCancelBookings = can(CAPABILITIES.BOOKINGS_CANCEL);
    const { bookingId } = useParams();
    const { notifyError, notifySuccess } = useNotify();
    const navigate = useNavigate();
    const { showModal } = useModals();
    const [operatingId, setOperatingId] = useState(null);
    const [requestTimeslotEdit, setRequestTimeslotEdit] = useState(false);

    // Lifted from BookingItemsDetails / EditBookingDetails / CustomFieldsDetails
    // so the page-level loading gate skips re-mount on refetch while the agent
    // is mid-edit (otherwise refocus / parallel reload wipes editor state).
    const [editorUnlocked, setEditorUnlocked] = useState(false);
    const [contactEdits, setContactEdits] = useState({});
    // null = no local edits (the block tracks the server note); an array = the
    // agent's working copy. Layering the draft over the server note avoids
    // clobbering edits on a background refetch and needs no seed effect.
    const [customFieldsDraft, setCustomFieldsDraft] = useState(null);

    const [itemsPendingChanges, setItemsPendingChanges] = useState(false);
    const [itemsSaveDisabled, setItemsSaveDisabled] = useState(false);

    const itemsRef = useRef();

    const buildContactPayload = (contact) => ({
        status: data?.booking?.status,
        venueId: data?.booking?.venueId,
        timeslotId: data?.booking?.timeslotId,
        firstName: contact.firstName,
        lastName: contact.lastName,
        phone: contact.phone,
        email: contact.email,
        language: contact.language,
        note: data?.booking?.note,
        checkinNote: contact.checkinNote,
        isReviewEmailSuppressed: contact.isReviewEmailSuppressed,
        notificationChannels: contact.notificationChannels,
        agentId: data?.booking?.agentId,
    });

    const buildContactChangeList = (contact) => {
        const booking = data?.booking;
        if (!booking) return [];
        const changes = [];
        const simpleFields = [
            ['firstName', t('bookings.columns.firstName')],
            ['lastName', t('bookings.columns.lastName')],
            ['phone', t('bookings.columns.phone')],
            ['email', t('bookings.columns.email')],
        ];
        for (const [field, label] of simpleFields) {
            if ((contact[field] || '') !== (booking[field] || '')) {
                changes.push(`${label}: ${booking[field] || '-'} → ${contact[field] || '-'}`);
            }
        }
        if (contact.language !== booking.language) {
            changes.push(
                `${t('bookings.columns.language')}: ${booking.language?.toUpperCase() || '-'} → ${contact.language?.toUpperCase() || '-'}`,
            );
        }
        if (Boolean(contact.isReviewEmailSuppressed) !== Boolean(booking.isReviewEmailSuppressed)) {
            changes.push(
                `${t('bookings.columns.isReviewEmailSuppressed')}: ${t(contact.isReviewEmailSuppressed ? 'common.enabled' : 'common.disabled')}`,
            );
        }
        const oldEmailCh = booking.notificationChannels?.includes('email') ?? true;
        const newEmailCh = contact.notificationChannels?.includes('email') ?? true;
        if (oldEmailCh !== newEmailCh) {
            changes.push(
                `${t('bookings.columns.emailNotifications')}: ${t(newEmailCh ? 'common.enabled' : 'common.disabled')}`,
            );
        }
        const oldSmsCh = booking.notificationChannels?.includes('sms') ?? false;
        const newSmsCh = contact.notificationChannels?.includes('sms') ?? false;
        if (oldSmsCh !== newSmsCh) {
            changes.push(
                `${t('bookings.columns.smsNotifications')}: ${t(newSmsCh ? 'common.enabled' : 'common.disabled')}`,
            );
        }
        if ((contact.checkinNote || '') !== (booking.checkinNote || '')) {
            changes.push(`${t('bookings.columns.checkinNote')} ${t('common.changed')}`);
        }
        return changes;
    };

    const handleSharedSave = async () => {
        if (contactDirty && !contactValid) {
            notifyError(t('errors.email'));
            return;
        }
        if (customFieldsDirty && !customFieldsValid) {
            notifyError(t('common.requiredFieldsAreNotFilled'));
            return;
        }
        // Contact and additional-info both PATCH the booking, so they merge
        // into ONE editBooking call below - otherwise the second write would
        // overwrite the first with stale values.
        const bookingDirty = contactDirty || customFieldsDirty;
        const contactChangeList = contactDirty ? buildContactChangeList(contact) : [];
        const customFieldsChangeList = customFieldsDirty
            ? [`${t('tickettypes.additionalInformation')} ${t('common.changed')}`]
            : [];
        const newEmail = contactDirty ? contact.email?.trim() : null;

        const buildBookingPayload = () => {
            const payload = buildContactPayload(contact);
            if (!payload) return null;
            if (customFieldsDirty) {
                payload.note = buildNotePayload(customFields);
            }
            return payload;
        };

        const itemsData = itemsPendingChanges ? await itemsRef.current?.prepareConfirmData() : null;
        // Ticket prep returns null after surfacing its own error (auto-seat
        // failure, commission-group restrictions). Abort the whole save so we
        // don't PATCH the booking and report success while silently dropping the
        // ticket changes the agent asked for.
        if (itemsPendingChanges && !itemsData) return;

        const itemsChangeList = itemsData?.changeList ?? [];
        const allChangeList = [...contactChangeList, ...customFieldsChangeList, ...itemsChangeList];

        if (!bookingDirty && !itemsPendingChanges) return;

        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('bookingitems.modifyTickets.title'),
            content: (
                <React.Fragment>
                    <ul>
                        {allChangeList.map((change, index) => (
                            <li key={index}>{change}</li>
                        ))}
                    </ul>
                </React.Fragment>
            ),
            warningContent:
                (itemsData &&
                    itemsData.oldPriceSum !== itemsData.newPriceSum &&
                    t(
                        itemsData.isNetRate
                            ? 'bookingitems.modifyTickets.priceChangeNetRate'
                            : 'bookingitems.modifyTickets.priceChange',
                        {
                            oldPrice: formatCentsToUsd(itemsData.oldPriceSum),
                            newPrice: formatCentsToUsd(itemsData.newPriceSum),
                            oldFinalPriceSum: formatCentsToUsd(itemsData.oldFinalPriceSum),
                            newFinalPriceSum: formatCentsToUsd(itemsData.newFinalPriceSum),
                        },
                    )) ||
                '',
            errorContent: itemsData?.onlyCanBuyAloneTickets ? t('errors.cannotBuyAlone') : '',
            sendNotificationFlag: !!itemsPendingChanges,
            showCouponInput: itemsData?.isUpgrade ?? false,
            showId: itemsData?.showId,
            customerEmail: itemsData?.customerEmail ?? data?.booking?.email ?? '',
            orderAmount: itemsData?.additionalAmount ?? 0,
            confirm: async (sendNotification, couponCode) => {
                try {
                    // Booking PATCH (contact + additional info) first, then
                    // items. There is no cross-call transaction, so abort
                    // before the items write if the booking write fails rather
                    // than persisting half the change while reporting success.
                    if (bookingDirty) {
                        const bookingPayload = buildBookingPayload();
                        if (bookingPayload) {
                            await editBooking(Number(bookingId), bookingPayload);
                        }
                    }
                    if (itemsPendingChanges && itemsData) {
                        await itemsRef.current?.executeSave(
                            itemsData.itemsToSubmit,
                            itemsData.timeslotChanged,
                            couponCode,
                            sendNotification,
                        );
                    }
                } catch (err) {
                    notifyError(err);
                    reloadData();
                    return;
                }

                // One success surface and one refresh for the whole save
                // (reloadData invalidates /orders and /bookingitems too).
                notifySuccess(t('bookings.notifications.updated'));
                reloadData();

                if (
                    contactDirty &&
                    newEmail &&
                    newEmail !== data?.booking?.email &&
                    latestEmailLog
                ) {
                    showModal(MODAL_TYPE.CONFIRMATION, {
                        title: t('bookings.resendOnContactChange.title'),
                        content: t('bookings.resendOnContactChange.message', {
                            subject: latestEmailLog.subject,
                            email: newEmail,
                        }),
                        confirm: async () => {
                            try {
                                await resendEmail(latestEmailLog.id, { to: newEmail });
                                notifySuccess(t('emaillogs.emailHasBeenSentAgain'));
                            } catch (err) {
                                notifyError(err);
                            }
                        },
                    });
                }
            },
        });
    };

    const handleSharedDiscard = () => {
        if (contactDirty) setContactEdits({});
        if (customFieldsDirty) setCustomFieldsDraft(null);
        if (itemsPendingChanges) itemsRef.current?.handleDiscardChanges();
    };

    const {
        data,
        isFetching: loading,
        error: bookingError,
        refetch: reloadBookings,
    } = useGetBooking(Number(bookingId), {
        query: {
            onError: (err) => {
                notifyError(err);
                navigate('/bookings');
            },
        },
    });

    // Custom-field definitions come from the booking items; the page owns this
    // query so it can build the additional-info baseline (CustomFieldsDetails is
    // a controlled child).
    const { data: bookingItemsData, isLoading: customFieldsLoading } = useGetBookingItems(
        {
            optionalId: Number(bookingId),
            pageSize: 100,
            sort: 'created_at',
            sortDir: 'ASC',
        },
        {
            query: {
                onError: (err) => {
                    notifyError(err);
                    navigate('/bookings');
                },
            },
        },
    );

    const {
        data: emailLogData,
        isFetching: emailLoading,
        refetch: reloadEmailLogs,
    } = useGetEmailLogs({
        bookingId,
    });

    const latestEmailLog = (emailLogData?.emaillogs || []).reduce(
        (latest, e) => (!latest || e.id > latest.id ? e : latest),
        null,
    );

    const {
        data: ordersData,
        isLoading: ordersLoading,
        refetch: reloadOrders,
    } = useGetOrders({
        optionalId: Number(bookingId),
        pageSize: 10000,
        // Transactions are required so PaymentModal can subtract already-finished
        // ones (e.g. a coupon transaction) from the remaining amount. Without
        // this the top "Pay" button shows the full order amount even after a
        // coupon has been applied.
        includeTransactions: true,
    });

    const {
        refetch: reloadLogs,
        data: logsData,
        isLoading: logsLoading,
    } = useGetBookingAuditLogs(Number(bookingId));

    const { ticketTypes, ticketOptions } = useTicketMatrixData({
        showId: data?.booking?.timeslot?.showId,
    });

    const queryClient = useQueryClient();
    const reloadData = useCallback(() => {
        reloadBookings();
        reloadLogs();
        reloadEmailLogs();
        // BookingItemsDetails owns its own GET /bookingitems query — invalidate
        // it so the items table refreshes when, e.g., a pending modification is
        // rolled back from OrdersDetails.
        queryClient.invalidateQueries({ queryKey: ['/bookingitems'] });
        queryClient.invalidateQueries({ queryKey: ['/orders'] });
    }, [reloadBookings, reloadLogs, reloadEmailLogs, queryClient]);

    const firstPendingOrder = ordersData?.orders?.find(
        (order) => order.status === ORDER_STATUSES.PENDING,
    );
    const hasAnyPendingOrders = ordersLoading || !!firstPendingOrder;

    const cancelReservation = useCallback(
        async (id, amount, { overrideCancelPolicy = false } = {}) => {
            setOperatingId(id);
            try {
                await cancelBooking(id, {
                    amount,
                    reservationHash: data?.reservationHash,
                    overrideCancelPolicy,
                });
                notifySuccess(t('bookings.notifications.cancelReservation'));
                reloadData();
            } catch (_error) {
                notifyError(t('bookings.notifications.cancelReservationError'));
            } finally {
                setOperatingId(null);
            }
        },
        [notifyError, notifySuccess, reloadData, t, data?.reservationHash],
    );

    const refundReservation = useCallback(
        async (id, amount) => {
            setOperatingId(id);
            try {
                await simpleRefundBooking(id, { amount });
                notifySuccess(t('common.refundReservation'));
                reloadData();
            } catch (_error) {
                notifyError(t('errors.refundReservationError'));
            } finally {
                setOperatingId(null);
            }
        },
        [notifyError, notifySuccess, reloadData, t],
    );

    const baseContact = pickContactFields(data?.booking);
    const contact = { ...baseContact, ...contactEdits };
    const contactDirty =
        !!data?.booking &&
        (contact.firstName !== baseContact.firstName ||
            contact.lastName !== baseContact.lastName ||
            contact.phone !== baseContact.phone ||
            contact.email !== baseContact.email ||
            contact.language !== baseContact.language ||
            contact.checkinNote !== baseContact.checkinNote ||
            Boolean(contact.isReviewEmailSuppressed) !==
                Boolean(baseContact.isReviewEmailSuppressed) ||
            !sameChannels(contact.notificationChannels, baseContact.notificationChannels));
    const contactValid = EMAIL_SCHEMA.isValidSync(contact.email);

    // Additional-info draft layered over the server note, same pattern as contact.
    const customFieldsBaseline = mapCustomFields(
        bookingItemsData?.bookingItems,
        data?.booking,
        data?.noteAgents,
    );
    const customFieldsBaselineKey = noteKey(customFieldsBaseline);
    const customFields = customFieldsDraft ?? customFieldsBaseline;
    const customFieldsDirty =
        customFieldsDraft !== null && noteKey(customFieldsDraft) !== customFieldsBaselineKey;
    // A required field left empty by the guest must not block the agent from
    // saving an unrelated edit. Only the agent emptying a required field (one
    // that had a value on the server) blocks the save.
    const baselineRequiredEmpty = new Set(
        customFieldsBaseline.filter((field) => field.required && !field.value).map((f) => f.name),
    );
    const customFieldsValid = !customFields.some(
        (field) => field.required && !field.value && !baselineRequiredEmpty.has(field.name),
    );

    const anyEditorOpen = editorUnlocked || contactDirty || customFieldsDirty;

    // Drop the local draft whenever the PERSISTED value changes - initial load,
    // our own save, or an external edit - so the form re-syncs to exactly what the
    // server returned. We must NOT instead "clear once the draft matches the
    // server", because the backend can round-trip a saved value into a slightly
    // different shape (e.g. a note's agent id, or a normalized field) that never
    // byte-matches the draft, which would leave the block stuck dirty forever and
    // block the save / refresh. Untouched drafts track the server (contact merges
    // over `baseContact`, additional-info falls back to its baseline), so no seed
    // effect is needed; an in-flight save keeps the draft until its refetch lands.
    const contactSyncKey = JSON.stringify(baseContact);
    useEffect(() => {
        setContactEdits({});
    }, [contactSyncKey]);

    useEffect(() => {
        setCustomFieldsDraft(null);
    }, [customFieldsBaselineKey]);

    const hasUnsavedChanges = contactDirty || customFieldsDirty || itemsPendingChanges;
    // Drop the guard once the booking failed to load: the error path below
    // redirects to /bookings, and an active guard would intercept that redirect
    // and loop until React hits its max update depth.
    const guardActive = hasUnsavedChanges && !bookingError;
    // F5 / tab close - native browser prompt (only thing possible on real unload).
    useUnsavedChangesWarning(guardActive);
    // In-app navigation + the browser back/forward button - a styled confirm modal.
    useNavigationGuard(guardActive, (proceed) =>
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('bookings.unsavedLeaveTitle'),
            content: t('bookings.unsavedLeaveMessage'),
            confirm: proceed,
        }),
    );
    // Logout swaps the tree by nulling the user instead of navigating, so the
    // router guard can't see it; publish the dirty flag for the shared guard the
    // logout button consults.
    const { setDirty } = useUnsavedChanges();
    useEffect(() => {
        setDirty(guardActive);
        return () => setDirty(false);
    }, [guardActive, setDirty]);

    if (bookingError) {
        navigate('/bookings');
        return null;
    }

    if ((loading && !anyEditorOpen) || !data?.booking) {
        return null;
    }

    const guestName = [data?.booking?.firstName, data?.booking?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();

    const rebookedTo = data?.booking?.rebookedTo || null;
    const rebookedFrom = data?.booking?.rebookedFrom || null;
    const isRebookedTo = rebookedTo && /^\d+$/.test(rebookedTo);
    const isRebookedFrom = rebookedFrom && /^\d+$/.test(rebookedFrom);

    const handleRebookedNavigate = async (ref) => {
        if (!ref) return;
        try {
            const res = await getBookings({ confirmationCode: ref, pageSize: 1 });
            const target = res?.bookings?.[0];
            if (target) navigate(`/bookings/edit/${target.id}`);
        } catch {
            // silently fail — user can search manually
        }
    };

    return (
        <PageWithTitle
            title={`${t('bookings.bookingDetails', {
                bookingId: data?.booking?.id || bookingId,
            })}${guestName ? ` · ${guestName}` : ''}`}
            headline={[
                <span
                    key="status"
                    style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 4,
                    }}
                >
                    <span style={{ position: 'relative', display: 'inline-flex' }}>
                        <Chip
                            style={{
                                background: BOOKING_STATUS_COLORS[data?.booking?.status] || '#ccc',
                                color: '#fff',
                            }}
                            label={t(`widgetReservationInfo.statusValues.${data?.booking?.status}`)}
                        />
                        {(rebookedTo || rebookedFrom) && (
                            <AutorenewIcon
                                sx={{
                                    position: 'absolute',
                                    top: -5,
                                    right: -5,
                                    fontSize: 17,
                                    color: BOOKING_STATUS_COLORS[data?.booking?.status] || '#ccc',
                                    backgroundColor: '#fff',
                                    borderRadius: '50%',
                                    border: `1.5px solid ${BOOKING_STATUS_COLORS[data?.booking?.status] || '#ccc'}`,
                                    padding: '1px',
                                }}
                            />
                        )}
                    </span>
                    {isRebookedTo && (
                        <span
                            onClick={() => handleRebookedNavigate(rebookedTo)}
                            style={{
                                fontSize: 12,
                                color: BOOKING_STATUS_COLORS[data?.booking?.status] || '#d9534f',
                                cursor: 'pointer',
                            }}
                        >
                            {t('bookings.rebookedTo', { code: rebookedTo })}
                        </span>
                    )}
                    {isRebookedFrom && (
                        <span
                            onClick={() => handleRebookedNavigate(rebookedFrom)}
                            style={{
                                fontSize: 12,
                                color: BOOKING_STATUS_COLORS[data?.booking?.status] || '#d9534f',
                                cursor: 'pointer',
                            }}
                        >
                            {t('bookings.rebookedFrom', { code: rebookedFrom })}
                        </span>
                    )}
                </span>,
            ]}
        >
            <Grid2 container spacing={2} size={{ xs: 12 }} alignItems="center">
                <Grid2 size={{ xs: 12, sm: 4 }} textAlign={'left'}>
                    <b>{data?.booking?.timeslot.showTitle}</b>
                    {' · '}
                    {data?.booking?.venueName}
                </Grid2>
                <Grid2
                    size={{ xs: 12, sm: 4 }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        minHeight: 48,
                    }}
                >
                    {(contactDirty || customFieldsDirty || itemsPendingChanges) && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: 'background.paper',
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 2,
                                boxShadow: 1,
                            }}
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                {t('bookingitems.unsavedChanges')}
                            </Typography>
                            <Button
                                variant="text"
                                onClick={handleSharedDiscard}
                                sx={{ minWidth: 0 }}
                            >
                                {t('common.discard')}
                            </Button>
                            <Tooltip
                                title={
                                    itemsSaveDisabled
                                        ? t('bookingitems.saveBlockedByCapacity')
                                        : contactDirty && !contactValid
                                          ? t('errors.email')
                                          : customFieldsDirty && !customFieldsValid
                                            ? t('common.requiredFieldsAreNotFilled')
                                            : ''
                                }
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        onClick={handleSharedSave}
                                        disabled={
                                            itemsSaveDisabled ||
                                            (contactDirty && !contactValid) ||
                                            (customFieldsDirty && !customFieldsValid)
                                        }
                                    >
                                        {t('common.save')}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                </Grid2>
                <Grid2
                    size={{ xs: 12, sm: 4 }}
                    textAlign={'right'}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                >
                    {data?.booking?.timeslot?.startTime
                        ? formatDateTimeInVenueTz(data?.booking?.timeslot?.startTime)
                        : ''}
                    {canManageBookings &&
                        !data?.booking?.limitedModification &&
                        !data?.booking?.invoiceId &&
                        data?.booking?.status !== BOOKING_STATUSES.CANCELLED && (
                            <Tooltip
                                title={
                                    hasAnyPendingOrders
                                        ? t('bookingitems.warningPendingOrders')
                                        : t('bookings.changeDate')
                                }
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        sx={{ ml: 0.5 }}
                                        onClick={() => setRequestTimeslotEdit(true)}
                                        disabled={hasAnyPendingOrders}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                </Grid2>
            </Grid2>
            {canManageBookings && firstPendingOrder && (
                <Grid2 size={{ xs: 12 }}>
                    <Alert
                        severity="error"
                        action={
                            <CommonButton
                                color="inherit"
                                onClick={() => {
                                    showModal(MODAL_TYPE.PAYMENT, {
                                        title: t('paymentModal.title'),
                                        content: t('paymentModal.content'),
                                        order: firstPendingOrder,
                                        allOrders: ordersData?.orders || [],
                                        paymentMethods: data?.paymentMethods,
                                        reservationId: bookingId,
                                        reservationHash: data?.reservationHash,
                                        reloadData: reloadOrders,
                                        venueAllowSplitPayment: data?.venueAllowSplitPayment,
                                        reloadBookingData: reloadData,
                                        absorbProcessingFee: data?.absorbProcessingFee,
                                    });
                                }}
                                label={t('common.pay')}
                            />
                        }
                    >
                        {t('bookings.unpaidOrdersWarning')}
                    </Alert>
                </Grid2>
            )}
            {data?.booking?.limitedModification && (
                <Grid2 size={{ xs: 12 }}>
                    <Alert severity="warning" sx={{ '& .MuiAlert-message': { textAlign: 'left' } }}>
                        {t('bookings.limitedModificationWarning')}
                    </Alert>
                </Grid2>
            )}
            {data?.booking?.invoiceId && (
                <Grid2 size={{ xs: 12 }}>
                    <Alert severity="warning" sx={{ '& .MuiAlert-message': { textAlign: 'left' } }}>
                        {t('bookings.invoicedBookingWarning')}
                    </Alert>
                </Grid2>
            )}
            <Grid2 container spacing={2} size={{ xs: 12 }}>
                <Grid2 size={{ xs: 12, sm: 4 }} spacing={2} container alignContent={'flex-start'}>
                    <EditBookingDetails
                        bookingId={bookingId}
                        booking={data?.booking}
                        reloadData={reloadData}
                        readOnly={!canManageBookings}
                        contact={contact}
                        onContactChange={(field, value) =>
                            setContactEdits((edits) => ({ ...edits, [field]: value }))
                        }
                        emailError={!contactValid}
                    />
                    <CustomFieldsDetails
                        booking={data?.booking}
                        readOnly={!canManageBookings}
                        loading={customFieldsLoading}
                        fields={customFields}
                        onFieldsChange={(updater) =>
                            setCustomFieldsDraft((prev) => updater(prev ?? customFieldsBaseline))
                        }
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 8 }} spacing={2} container alignContent={'flex-start'}>
                    <BookingItemsDetails
                        ref={itemsRef}
                        bookingId={bookingId}
                        booking={data?.booking}
                        reloadData={reloadData}
                        reloadParentOrders={reloadOrders}
                        limitedModification={data?.booking?.limitedModification}
                        requestTimeslotEdit={!canManageBookings ? false : requestTimeslotEdit}
                        onTimeslotEditHandled={() => setRequestTimeslotEdit(false)}
                        readOnly={!canManageBookings}
                        bookingUnlocked={editorUnlocked}
                        setBookingUnlocked={setEditorUnlocked}
                        onPendingChangesChange={({ hasPendingChanges, saveDisabled }) => {
                            setItemsPendingChanges(hasPendingChanges);
                            setItemsSaveDisabled(saveDisabled);
                        }}
                        canCancel={
                            canCancelBookings &&
                            !data?.booking?.limitedModification &&
                            (data?.booking?.status === BOOKING_STATUSES.FINISHED ||
                                data?.booking?.status === BOOKING_STATUSES.PROCESSING)
                        }
                        canRefund={
                            canRefundBookings &&
                            !data?.booking?.limitedModification &&
                            data?.booking?.status === BOOKING_STATUSES.FINISHED
                        }
                        operatingDisabled={operatingId === Number(bookingId)}
                        onCancelReservation={() =>
                            showModal(MODAL_TYPE.CANCELLATION_CONFIRMATION, {
                                title: t('confirmationModal.title'),
                                content: t('confirmationModal.content'),
                                bookingId: bookingId,
                                startTime: data?.booking?.timeslot?.startTime,
                                cancelWindow: data?.booking?.timeslot?.cancelWindow,
                                cancelDescription: data?.booking?.timeslot?.cancelDescription,
                                alwaysRefund: canAlwaysRefund && !data?.booking?.invoiceId,
                                confirm: (id, amount, opts) => cancelReservation(id, amount, opts),
                            })
                        }
                        onRefundReservation={() =>
                            showModal(MODAL_TYPE.REFUND_CONFIRMATION, {
                                title: t('refundModal.title'),
                                content: t('refundModal.content'),
                                bookingId: bookingId,
                                startTime: data?.booking?.timeslot?.startTime,
                                cancelWindow: data?.booking?.timeslot?.cancelWindow,
                                confirm: (id, amount) => refundReservation(id, amount),
                            })
                        }
                    />
                    <OrdersDetails
                        bookingId={bookingId}
                        reservationHash={data?.reservationHash}
                        paymentMethods={data?.paymentMethods}
                        venueAllowSplitPayment={data?.venueAllowSplitPayment}
                        reloadBookingData={reloadData}
                        reloadParentOrders={reloadOrders}
                        limitedModification={data?.booking?.limitedModification}
                        collapsible
                        absorbProcessingFee={data?.absorbProcessingFee}
                    />
                    <BookingAuditLogsBlock
                        logs={logsData?.auditlogs}
                        loading={logsLoading}
                        ticketTypes={ticketTypes}
                        ticketOptions={ticketOptions}
                        collapsible
                    />
                    <BookingEmailLogsBlock
                        emails={emailLogData?.emaillogs}
                        loading={emailLoading}
                        reloadData={reloadData}
                        collapsible
                    />
                </Grid2>
            </Grid2>
        </PageWithTitle>
    );
}

// The router renders a single <EditBookingPage /> element, so React reuses the
// same instance across /bookings/edit/:bookingId changes. Key the inner page by
// bookingId to force a fresh mount per booking - otherwise one booking's lifted
// edit drafts (contact / additional-info / items) leak onto the next and show as
// spurious "unsaved changes".
function EditBookingPage() {
    const { bookingId } = useParams();
    return <EditBookingPageInner key={bookingId} />;
}

export default EditBookingPage;
